import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './PortfolioPreview.css';
import PortfolioDetail from '../PortfolioDetail/PortfolioDetail';
import OptimizedImage from '../OptimizedImage/OptimizedImage';

interface PortfolioItem {
  id: number;
  title: string;
  category: string;
  location: string;
  image: string;
  images?: string[];
  description: string;
  details: {
    year: string;
    area: string;
    client: string;
  };
  size: string;
  offsetY: string;
}

const PortfolioPreview: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<PortfolioItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const featuredProjects: PortfolioItem[] = [
    {
      id: 1,
      title: "IEVOKE",
      category: "Commercial",
      location: "Bandung",
      image: "/assets/images/ievoke/ievoke_3_compressed.png",
      images: [
        "/assets/images/ievoke/ievoke_1_compressed.png",
        "/assets/images/ievoke/ievoke_2_compressed.png",
        "/assets/images/ievoke/ievoke_3_compressed.png",
        "/assets/images/ievoke/ievoke_4_compressed.png",
        "/assets/images/ievoke/ievoke_5_compressed.png"
      ],
      description: "Four-floor sport center with boxing, pilates, yoga, and gym facilities. Designed with a natural concept and a classic touch, combining earthy colors and timeless details. The result is a serene yet sophisticated environment for both training and social interaction.",
      details: {
        year: "2025",
        area: "1300 m²",
        client: "IEVOKE Fitness"
      },
      size: "large",
      offsetY: "center"
    },
    {
      id: 2,
      title: "Meruya Boarding House",
      category: "Residential-Commercial",
      location: "Jakarta",
      image: "/assets/images/kost_menara/kost_menara_2_compressed.png",
      images: [
        "/assets/images/kost_menara/kost_menara_1_compressed.png",
        "/assets/images/kost_menara/kost_menara_2_compressed.png",
        "/assets/images/kost_menara/kost_menara_3_compressed.png",
        "/assets/images/kost_menara/kost_menara_4_compressed.png",
        "/assets/images/kost_menara/kost_menara_5_compressed.png"
      ],
      description: "Six-floor boarding house with a modern minimalist design. Japandi-inspired interiors with warm wood tones create an apartment-like feel. Neutral colors and soft textures make the space inviting, giving residents a comfortable and stylish place to live.",
      details: {
        year: "2025",
        area: "1100 m²",
        client: "Private Developer"
      },
      size: "medium",
      offsetY: "up"
    },
    {
      id: 3,
      title: "Ultima Padel",
      category: "Commercial",
      location: "Bandung",
      image: "/assets/images/ultima_padel/ultima_padel_1_compressed.png",
      images: [
        "/assets/images/ultima_padel/ultima_padel_1_compressed.png",
        "/assets/images/ultima_padel/ultima_padel_2_compressed.png",
        "/assets/images/ultima_padel/ultima_padel_3_compressed.png",
        "/assets/images/ultima_padel/ultima_padel_4_compressed.png",
        "/assets/images/ultima_padel/ultima_padel_5_compressed.png"
      ],
      description: "Modern padel court design with clean lines and contemporary materials. The layout prioritizes player flow and visibility, creating an engaging and premium sports experience for all visitors.",
      details: {
        year: "2025",
        area: "725 m²",
        client: "Ultima Sports"
      },
      size: "xl",
      offsetY: "center"
    },
    {
      id: 4,
      title: "Eclipse",
      category: "Commercial",
      location: "Jakarta",
      image: "/assets/images/eclipse/eclipse_1_compressed.png",
      images: [
        "/assets/images/eclipse/eclipse_1_compressed.png",
        "/assets/images/eclipse/eclipse_2_compressed.png",
        "/assets/images/eclipse/eclipse_3_compressed.png",
        "/assets/images/eclipse/eclipse_4_compressed.png",
        "/assets/images/eclipse/eclipse_5_compressed.png"
      ],
      description: "Game center with a dark, futuristic look. RGB lighting sets an immersive and energetic atmosphere that enhances the gaming experience. Sleek finishes and bold forms give the space a modern, high-tech character.",
      details: {
        year: "2025",
        area: "236 m²",
        client: "Eclipse Gaming"
      },
      size: "small",
      offsetY: "down"
    }
  ];

  const handleProjectClick = (project: PortfolioItem) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleNavigateProject = (direction: 'prev' | 'next') => {
    if (!selectedProject) return;

    const currentIndex = featuredProjects.findIndex(p => p.id === selectedProject.id);
    let nextIndex;

    if (direction === 'prev') {
      nextIndex = currentIndex === 0 ? featuredProjects.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === featuredProjects.length - 1 ? 0 : currentIndex + 1;
    }

    setSelectedProject(featuredProjects[nextIndex]);
  };

  return (
    <section className="portfolio-preview-wrapper">
      <div className="container">
        <div className="portfolio-preview-header">
          <div className="header-content">
            <h1>Featured <span id="gold">Projects</span></h1>
            <p>Explore some of our most recent architectural achievements that showcase our expertise in creating stunning, functional spaces.</p>
          </div>
          <Link to="/portfolio" className="view-all-btn">
            <div className="btn-text">
              <h4>View All Projects</h4>
            </div>
            <div className="btn-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14m-7-7 7 7-7 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </Link>
        </div>

        <div className="featured-projects-grid">
          {featuredProjects.map((project) => (
            <div
              key={project.id}
              className="project-card"
              onClick={() => handleProjectClick(project)}
              style={{ cursor: 'pointer' }}
            >
              <div className="project-image">
                <OptimizedImage src={project.image} alt={project.title} loading="eager" />
                <div className="project-overlay">
                  <div className="project-info">
                    <p>{project.category.toUpperCase()}</p>
                    <h3>{project.title}</h3>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Portfolio Detail Modal */}
      <PortfolioDetail
        item={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onNavigate={handleNavigateProject}
      />
    </section>
  );
};

export default PortfolioPreview;